import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const CategoryNavbar = ({ closeMenu }) => {
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null); // full object
  const [isOpen, setIsOpen] = useState(false); // dropdown open/close
  const wrapRef = useRef(null);

  useEffect(() => {
    axios
      .get("https://api.vyapakprint.com/api/categories/getCategories")
      .then((res) => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  // Close on outside click + Esc
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
        setActiveCat(null);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setActiveCat(null);
      }
    };

    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onEsc);
    };
  }, []);

  const openCategory = (cat) => {
    setActiveCat(cat);
    setIsOpen((cat?.subcategory?.length || 0) > 0);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setActiveCat(null);
  };

  // Desktop hover handlers (only meaningful on md+)
  const handleMouseEnter = (cat) => openCategory(cat);
  const handleMouseLeave = () => closeDropdown();

  const subs = activeCat?.subcategory || [];
  const showDropdown = isOpen && subs.length > 0;

  return (
    <div
      ref={wrapRef}
      className="bg-mainblue text-white font-semibold w-full relative"
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Categories row */}
        <div className="py-3">
          <ul className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar">
            {categories.slice(0, 8).map((cat) => {
              const isActive = activeCat?._id === cat._id && showDropdown;
              return (
                <li key={cat._id} className="shrink-0">
                  {/* Desktop: hover open | Mobile: tap toggle */}
                  <button
                    type="button"
                    onMouseEnter={() => handleMouseEnter(cat)}
                    onClick={() => {
                      // mobile/any click: toggle open for this category
                      if (activeCat?._id === cat._id && isOpen) {
                        closeDropdown();
                      } else {
                        openCategory(cat);
                      }
                    }}
                    className={`px-3 py-2 rounded-full transition text-sm md:text-[15px]
                      ${isActive ? "bg-white text-mainblue" : "bg-white/10 hover:bg-white hover:text-mainblue"}
                    `}
                  >
                    {cat.name}
                  </button>
                </li>
              );
            })}

            {/* Optional: View all categories link (if many) */}
            {categories.length > 8 && (
              <li className="shrink-0">
                <Link
                  to="/categories"
                  onClick={() => {
                    closeDropdown();
                    closeMenu?.();
                  }}
                  className="px-3 py-2 rounded-full bg-white/10 hover:bg-white hover:text-mainblue transition text-sm md:text-[15px]"
                >
                  View all
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute left-0 top-full w-full bg-white text-mainblue shadow-lg border-t border-gray-100 z-[999]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-base md:text-lg">
                {activeCat?.name}
              </div>

              {/* Close button for mobile */}
              <button
                type="button"
                onClick={closeDropdown}
                className="md:hidden px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition text-sm"
              >
                Close
              </button>
            </div>

            {/* Subcategories grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {subs.map((sub) => (
                <Link
                  key={sub._id}
                  to={`/subcategory/${sub._id}/${encodeURIComponent(sub.name)}`}
                  onClick={() => {
                    closeDropdown();
                    closeMenu?.(); // closes mobile menu from Navbar too
                  }}
                  className="px-3 py-2 rounded-lg hover:bg-gray-100 transition text-sm md:text-[14px] font-medium"
                >
                  {sub.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryNavbar;
